import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ONBOARDING_STEPS, OnboardingStepDef } from '../hooks/useOnboardingGuide';
import { CheckCircleIcon } from './Icons';

interface OnboardingGuideProps {
  completedSteps: string[];
  onSkip: () => void;
  onDismiss: () => void;
}

const PHASES = [
  { id: 1, title: '搭建组织架构' },
  { id: 2, title: '创建并推送问卷' },
];

const OnboardingGuide: React.FC<OnboardingGuideProps> = ({
  completedSteps,
  onSkip,
  onDismiss,
}) => {
  const navigate = useNavigate();
  const [activePhase, setActivePhase] = useState(1);

  const phaseSteps = ONBOARDING_STEPS.filter(s => s.phase === activePhase);
  const totalCompleted = completedSteps.length;
  const totalSteps = ONBOARDING_STEPS.length;
  const progressPercent = (totalCompleted / totalSteps) * 100;
  const allCompleted = totalCompleted === totalSteps;

  // Find first incomplete step in current phase for highlighting
  const firstIncompleteId = phaseSteps.find(s => !completedSteps.includes(s.id))?.id;

  const renderStep = (step: OnboardingStepDef, index: number) => {
    const isCompleted = completedSteps.includes(step.id);
    const isCurrent = step.id === firstIncompleteId;

    return (
      <div
        key={step.id}
        className={`flex items-center justify-between py-3 px-4 rounded-lg transition-colors ${
          isCurrent ? 'bg-surface-container-low' : ''
        }`}
      >
        <div className="flex items-center space-x-3">
          {/* Step indicator */}
          <div className="flex-shrink-0">
            {isCompleted ? (
              <div className="w-7 h-7 flex items-center justify-center text-green-500">
                <CheckCircleIcon size={22} />
              </div>
            ) : isCurrent ? (
              <div className="w-7 h-7 rounded-full border-2 border-primary-600 flex items-center justify-center">
                <span className="text-xs font-semibold text-primary-600">{index + 1}</span>
              </div>
            ) : (
              <div className="w-7 h-7 rounded-full border-2 border-gray-300 flex items-center justify-center">
                <span className="text-xs font-medium text-gray-400">{index + 1}</span>
              </div>
            )}
          </div>

          {/* Step text */}
          <div>
            <p className={`text-sm font-medium ${
              isCompleted ? 'text-gray-400 line-through' : 'text-gray-900'
            }`}>
              {step.title}
            </p>
            <p className="text-xs text-gray-500">{step.description}</p>
          </div>
        </div>

        {/* Navigate button */}
        {!isCompleted && (
          <button
            onClick={() => navigate(step.route)}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap ml-4"
          >
            前往 &rarr;
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="rounded-[2rem] bg-surface-container-lowest p-6 mb-8 custom-shadow">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <span className="text-lg">🚀</span>
          <h3 className="text-base font-semibold text-gray-900">快速上手指南</h3>
        </div>
        <div className="flex items-center space-x-3 text-sm">
          <button
            onClick={onSkip}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            稍后再说
          </button>
          <span className="text-gray-300">·</span>
          <button
            onClick={onDismiss}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            不再显示
          </button>
        </div>
      </div>

      {allCompleted ? (
        /* All steps completed */
        <div className="text-center py-4">
          <div className="text-green-500 mb-2">
            <CheckCircleIcon size={32} />
          </div>
          <p className="text-sm font-medium text-gray-900">所有步骤已完成！</p>
          <p className="text-xs text-gray-500 mt-1">您已完成系统初始配置，可以开始正常使用了</p>
          <button
            onClick={onDismiss}
            className="mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            关闭引导
          </button>
        </div>
      ) : (
        <>
          {/* Phase tabs */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700">
                第{activePhase === 1 ? '一' : '二'}阶段：{PHASES.find(p => p.id === activePhase)?.title}
              </span>
            </div>
            <div className="flex items-center space-x-1.5">
              {PHASES.map(phase => {
                const phaseCompleted = ONBOARDING_STEPS
                  .filter(s => s.phase === phase.id)
                  .every(s => completedSteps.includes(s.id));
                return (
                  <button
                    key={phase.id}
                    onClick={() => setActivePhase(phase.id)}
                    className={`w-2.5 h-2.5 rounded-full transition-colors ${
                      activePhase === phase.id
                        ? phaseCompleted ? 'bg-green-500' : 'bg-primary-600'
                        : phaseCompleted ? 'bg-green-300' : 'bg-gray-300'
                    }`}
                    title={`${phase.title}`}
                  />
                );
              })}
            </div>
          </div>

          {/* Steps list */}
          <div className="space-y-1">
            {phaseSteps.map((step, i) => renderStep(step, i))}
          </div>

          {/* Footer: progress bar + phase navigation */}
          <div className="mt-4 pt-4 border-t border-surface-container-high">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3 flex-1">
                <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-600 rounded-full transition-all duration-500"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <span className="text-xs text-gray-500 whitespace-nowrap">
                  {totalCompleted}/{totalSteps} 已完成
                </span>
              </div>

              <div className="ml-4">
                {activePhase === 1 ? (
                  <button
                    onClick={() => setActivePhase(2)}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    下一阶段 &rarr;
                  </button>
                ) : (
                  <button
                    onClick={() => setActivePhase(1)}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    &larr; 上一阶段
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OnboardingGuide;
